import { useState } from 'react'
import { EducationalCard as EducationalCardType } from '../types'

interface EducationalCardProps {
  card: EducationalCardType
  onComplete: (cardId: string) => void
}

export function EducationalCard({ card, onComplete }: EducationalCardProps) {
  const [showExplanation, setShowExplanation] = useState(false)

  const handleComplete = () => {
    onComplete(card.id)
  }

  const toggleExplanation = () => {
    setShowExplanation(!showExplanation)
  }

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'beginner':
        return 'bg-green-100 text-green-800 border-green-200'
      case 'intermediate':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200'
      case 'advanced':
        return 'bg-red-100 text-red-800 border-red-200'
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200'
    }
  }

  const getDifficultyText = (difficulty: string) => {
    switch (difficulty) {
      case 'beginner':
        return 'Базовый'
      case 'intermediate':
        return 'Средний'
      case 'advanced':
        return 'Продвинутый'
      default:
        return 'Неизвестно'
    }
  }

  return (
    <div 
      className="bg-white rounded-lg shadow-md p-6 border border-gray-200 mb-4"
      role="article"
      aria-labelledby={`card-title-${card.id}`}
    >
      {/* Header with title and difficulty badge */}
      <div className="flex justify-between items-start mb-4">
        <h3 
          id={`card-title-${card.id}`}
          className="text-xl font-semibold text-gray-900 leading-tight"
        >
          {card.title}
        </h3>
        <span 
          className={`px-3 py-1 text-sm font-medium rounded-full border ${getDifficultyColor(card.difficulty)}`}
          aria-label={`Уровень сложности: ${getDifficultyText(card.difficulty)}`}
        >
          {getDifficultyText(card.difficulty)}
        </span>
      </div>

      {/* Main content */}
      <div className="mb-4">
        <p className="text-lg text-gray-700 leading-relaxed mb-4">
          {card.content}
        </p>
      </div>

      {/* Soviet-era analogy */}
      <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-4">
        <div className="flex items-start">
          <div className="ml-3">
            <p className="text-sm font-medium text-blue-800 mb-1">
              Простыми словами:
            </p>
            <p className="text-sm text-blue-700 leading-relaxed">
              {card.analogy}
            </p>
          </div>
        </div>
      </div>

      {/* Explanation section */}
      {showExplanation && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
          <h4 className="font-medium text-gray-900 mb-2">Объяснение:</h4>
          <p className="text-sm text-gray-700 leading-relaxed">
            Эта аналогия помогает понять сложную технологию через знакомые вам понятия. 
            ИИ действительно работает похожим образом — он использует накопленные знания 
            для ответа на ваши вопросы, как опытный специалист в своей области.
          </p>
        </div>
      )}

      {/* Action buttons */}
      <div className="flex flex-col sm:flex-row gap-3">
        <button
          onClick={toggleExplanation}
          className="flex-1 px-4 py-3 text-sm font-medium text-blue-600 bg-white border border-blue-600 rounded-lg hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
        >
          {showExplanation ? 'Скрыть объяснение' : 'Почему это работает?'}
        </button>
        
        <button
          onClick={handleComplete}
          className="flex-1 px-4 py-3 text-sm font-medium text-white bg-green-600 border border-transparent rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors"
        >
          Понятно!
        </button>
      </div>
    </div>
  )
}